import { type Breadcrumb, Breadcrumbs } from '@axinom/mosaic-shared-components';
import React from 'react';
import {
  type RouteComponentProps,
  generatePath,
  type match,
  matchPath,
  useLocation,
} from 'react-router';
import { type RouteConfig, Routes as routes } from '../../routes';
import { Menu } from '../Menu/Menu';
import classes from './Header.module.scss';

/**
 * Calculates the Breadcrumb object that can be given to the Header component.
 */
export function useBreadcrumb(): Breadcrumb[] {
  const pagePaths = routes?.map((route) => route.path);

  const { pathname } = useLocation();

  const currentMatch = findCurrentMatch(pathname, pagePaths);

  return calculateBreadcrumb(currentMatch, routes);
}

/**
 * Finds the current match from the routes given on the pages parameter.
 * @param pathname The current location.pathname
 * @param pagePaths all registered page paths
 */
function findCurrentMatch(pathname: string, pagePaths: string[]): match | null {
  const matchingPath = pagePaths.reduce<match | null>((prev, path) => {
    const match = matchPath(pathname, { path, exact: true });
    return prev ?? match;
  }, null);
  return matchingPath;
}

/**
 * Calculates the Breadcrumb array needed by the Header component.
 * @param match The current match
 * @param routes The registered pages
 */
const calculateBreadcrumb = (
  match: RouteComponentProps['match'] | null,
  routes: RouteConfig[],
): Breadcrumb[] => {
  if (!match) {
    return [];
  }

  // Splitting the match by path pieces
  const splitMatch = match.path.split('/');

  const bc: Breadcrumb[] = [];
  while (splitMatch.length > 0) {
    const subPath = splitMatch.join('/');

    // try finding a registered page that matches the current subPath
    const page = routes.find((page) => page.path === subPath);

    if (page?.meta?.breadcrumb) {
      // If page exists and has a breadcrumb value defined, we will add it to the array
      bc.unshift({
        url: generatePath(subPath, match.params), // Generating the actual URL (including the params) for the breadcrumb link
        label: page.meta.breadcrumb(match.params), // Passing the match.params down to the label resolver
        params: [],
      });
    }

    // Removing the last path section of the match as well as the url
    splitMatch.pop();
  }

  return bc;
};

/**
 * Displays user
 * @example
 * <Header
 *  crumbs={[{label: 'Movies', url: '/movies', params: []}]}
 *  user={ userName: 'User Name' }>
 *  <div>CHILD ELEMENTS</div>
 * </Header>
 */
export const Header: React.FC = () => {
  const breadcrumbs = useBreadcrumb();

  return (
    <div className={classes.container}>
      <Breadcrumbs crumbs={breadcrumbs} />
      <Menu />
    </div>
  );
};
